import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useState } from "react"
import FuelDockAvailability from "src/main/MarinaProfile/Fuel/FuelDockAvailability"
import ListedPumpPrices from "src/main/MarinaProfile/Fuel/ListedPumpPrices"
import RequestFuelPricesModal from "src/main/MarinaProfile/Fuel/RequestFuelPricesModal"

import Button from "src/components/Button"
import DealTicket from "src/components/DockwaPlus/DealTicket"
import Tooltip from "src/components/Tooltip"

import { useTracker } from "src/hooks/use_tracker"

const FuelSection = ({
  fuel,
  lastUpdatedAt,
  hasActiveDockwaPlusSubscription,
  hasDockwaPlusFuelDeals,
  highestDiscountTemplate,
  fuelDockAvailability,
  fuelPricesStale,
  userId,
  marinaName,
  mcomId,
  marinaId,
  fuelTypes,
  mostRecentFuelPriceRequestActive,
}) => {
  const [isRequestFuelPricesModalOpen, setIsRequestFuelPricesModalOpen] =
    useState(false)
  const [recentRequestActive, setRecentRequestActive] = useState(
    mostRecentFuelPriceRequestActive
  )
  const tracker = useTracker()

  const handleRequestPricesModalOpen = ({ fuelPricesPresent }) => {
    tracker.trackEvent(
      "destination_detail:request_fuel_prices_clicked:modal_open",
      { ...trackingProperties, fuelPricesPresent }
    )
    setIsRequestFuelPricesModalOpen(true)
  }

  const DockwaPlusSignupLink = () => {
    if (hasActiveDockwaPlusSubscription) {
      return null
    }

    return (
      <a
        href="/account/settings/memberships"
        className="ml-1 cursor-pointer font-semibold text-blue-700"
      >
        Learn more and sign up
      </a>
    )
  }
  const TooltipRequestSent = () => (
    <Tooltip
      text={
        <div className="text-center font-semibold text-teal-600">
          <span>
            Your request for updated prices has been sent! Check back soon.
          </span>
        </div>
      }
      placement="top"
      maxWidth="200px"
    >
      <i className="fa-kit fa-info text-gray-600" aria-hidden="true" />
    </Tooltip>
  )

  const UpdatePricesLink = () => (
    <div className="flex self-end">
      <Button
        variant="link"
        onClick={() =>
          handleRequestPricesModalOpen({ fuelPricesPresent: fuel?.length > 0 })
        }
      >
        Request Updated Prices
      </Button>
    </div>
  )

  const FuelPriceRequestCta = () => {
    if (recentRequestActive) {
      return <TooltipRequestSent />
    }
    return <UpdatePricesLink />
  }

  const FuelDealCta = () => (
    <div className="mb-6 flex w-full items-center justify-between space-x-2 text-end">
      {highestDiscountTemplate && (
        <DealTicket
          discount={`Up to ${highestDiscountTemplate.readableDiscount}`}
          variant="fuel"
        />
      )}
      <span className="text-sm">
        Deals available for Dockwa+ members.
        <br />
        <DockwaPlusSignupLink />
      </span>
    </div>
  )

  const LastUpdatedInfo = () => {
    const containerClasses = classNames("flex items-center space-x-2", {
      "flex-col space-y-2 lg:flex-row lg:space-y-0": !recentRequestActive,
    })

    return (
      <div className={containerClasses}>
        <span className="text-gray-600">Last updated: {lastUpdatedAt}</span>
        {fuelPricesStale && <FuelPriceRequestCta />}
      </div>
    )
  }

  const trackingProperties = { userId, mcomId, marinaName }
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="mb-0 text-lg font-bold">Fuel</h2>
        {lastUpdatedAt && <LastUpdatedInfo />}
      </div>
      {hasDockwaPlusFuelDeals && <FuelDealCta />}
      <div className="flex flex-col space-x-0 space-y-4 md:flex-row md:justify-between md:space-x-4 md:space-y-0">
        <ListedPumpPrices
          fuel={fuel}
          highestDiscountTemplate={highestDiscountTemplate}
          setIsRequestFuelPricesModalOpen={setIsRequestFuelPricesModalOpen}
          marinaName={marinaName}
          recentRequestActive={recentRequestActive}
          fuelDockAvailabilityEnabled={fuelDockAvailability?.enabled}
          handleRequestPricesModalOpen={handleRequestPricesModalOpen}
        />
        {fuelDockAvailability?.enabled && (
          <FuelDockAvailability fuelDockAvailability={fuelDockAvailability} />
        )}
        <RequestFuelPricesModal
          isOpen={isRequestFuelPricesModalOpen}
          handleClose={() => setIsRequestFuelPricesModalOpen(false)}
          trackingProperties={trackingProperties}
          fuelTypes={fuelTypes}
          marinaId={marinaId}
          setRecentRequestActive={setRecentRequestActive}
        />
      </div>
      <div className="my-6 flex w-full border-b" />
    </div>
  )
}

FuelSection.propTypes = {
  fuel: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      priceDollars: PropTypes.string.isRequired,
      effectivePriceDollars: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      discounted: PropTypes.bool.isRequired,
      readableDiscount: PropTypes.string,
    })
  ),
  lastUpdatedAt: PropTypes.string,
  hasActiveDockwaPlusSubscription: PropTypes.bool.isRequired,
  hasDockwaPlusFuelDeals: PropTypes.bool.isRequired,
  highestDiscountTemplate: PropTypes.shape({
    id: PropTypes.string.isRequired,
    readableDiscount: PropTypes.string.isRequired,
    percentAmount: PropTypes.string,
    amount: PropTypes.number,
  }),
  fuelDockAvailability: PropTypes.object.isRequired,
  fuelPricesStale: PropTypes.bool.isRequired,
  marinaName: PropTypes.string.isRequired,
  mcomId: PropTypes.string.isRequired,
  userId: PropTypes.number,
  fuelTypes: PropTypes.array.isRequired,
  marinaId: PropTypes.number.isRequired,
  mostRecentFuelPriceRequestActive: PropTypes.bool.isRequired,
}

export default FuelSection
